import {
  MOVE_THROTTLE_MS,
  SPEECH_BUBBLE_MS,
  TILE_SIZE,
  WHISPER_RADIUS_TILES,
  type Avatar,
  type Facing,
} from '@sokdaq/protocol';
import {
  NO_INPUT,
  OFFICE_MAP,
  findObject,
  inputFromVector,
  lerpToward,
  mergeInput,
  objectNear,
  stepActor,
  zoneAt,
  type Actor,
  type MapObject,
  type MoveInput,
  type Point,
} from '@sokdaq/room-core';
import { useChatStore } from '../store/chat.js';
import { drawAvatar, drawEmote, drawNameTag, drawSpeechBubble } from './draw-avatar.js';
import { drawObject, drawTile, visibleTileRange } from './draw-tiles.js';
import { PALETTE } from './palette.js';

const T = TILE_SIZE;

/** 가로로 최소한 이만큼의 타일이 보이도록 확대율을 정한다. */
const MIN_VISIBLE_TILES_X = 14;

/** 상호작용 이모트가 머리 위에 떠 있는 시간. */
const EMOTE_DURATION_MS = 4_000;

export interface SceneHandle {
  destroy(): void;
  /** 채팅 입력 중에는 이동 키를 넘겨줘야 한다. */
  setInputEnabled(enabled: boolean): void;
  setWhisperMode(on: boolean): void;
  /**
   * 조이스틱 방향. 중심 기준 반지름 1로 정규화한 벡터.
   *
   * 손을 떼면 (0, 0)을 보낸다. 키보드와 함께 쓸 수 있다 — 둘을 합친다.
   */
  setStickVector(dx: number, dy: number): void;
  /** 옆에 있는 오브젝트와 상호작용한다. 없으면 아무 일도 없다. */
  interactNearby(): void;
}

export interface SceneOptions {
  readonly canvas: HTMLCanvasElement;
  /** 손이 닿는 오브젝트가 바뀔 때만 부른다. 매 프레임이 아니다. */
  readonly onNearbyObjectChange: (object: MapObject | null) => void;
}

/** MoveInput은 읽기 전용이다. 키 상태는 매 프레임 갱신해야 하므로 푼다. */
type MutableInput = { -readonly [K in keyof MoveInput]: MoveInput[K] };

interface RemoteActor {
  /** 화면에 그리는 좌표. 목표를 향해 부드럽게 따라간다. */
  current: Point;
  target: Point;
  facing: Facing;
  walkPhase: number;
}

/**
 * 오피스 뷰의 렌더 루프.
 *
 * React 밖에서 돈다. 매 프레임 상태를 스토어에서 직접 읽고 캔버스에 그린다.
 * 이걸 컴포넌트 상태로 올리면 초당 60번 리렌더가 일어난다.
 */
export function createScene({ canvas, onNearbyObjectChange }: SceneOptions): SceneHandle {
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('2D 컨텍스트를 만들 수 없습니다');
  // 선언 타입을 non-null로 못박는다. 좁히기만 해두면 아래 함수 선언들이
  // 호이스팅되면서 좁힌 결과를 잃는다.
  const ctx: CanvasRenderingContext2D = context;

  const input: MutableInput = { ...NO_INPUT };
  /** 조이스틱에서 온 방향. 키보드 입력과 합쳐서 쓴다. */
  let stickInput: MoveInput = NO_INPUT;
  let inputEnabled = true;
  let whisperMode = false;

  let me: Actor | null = null;
  let myWalkPhase = 0;
  const remotes = new Map<string, RemoteActor>();

  let lastFrameAt = performance.now();
  let lastMoveSentAt = 0;
  let lastSentKey = '';
  let nearbyObject: MapObject | null = null;

  // ─── 입력 ──────────────────────────────────────────────────

  const KEYS: Record<string, keyof MoveInput> = {
    KeyW: 'up',
    KeyA: 'left',
    KeyS: 'down',
    KeyD: 'right',
    ArrowUp: 'up',
    ArrowLeft: 'left',
    ArrowDown: 'down',
    ArrowRight: 'right',
  };

  function onKeyDown(event: KeyboardEvent) {
    if (!inputEnabled) return;
    const key = KEYS[event.code];
    if (key === undefined) return;
    event.preventDefault();
    input[key] = true;
  }

  function onKeyUp(event: KeyboardEvent) {
    const key = KEYS[event.code];
    if (key === undefined) return;
    // 입력이 꺼진 상태에서도 뗀 건 반영한다. 안 그러면 채팅창으로
    // 포커스가 옮겨간 순간 누르고 있던 방향으로 계속 걷는다.
    input[key] = false;
  }

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  // 창이 포커스를 잃으면 keyup을 못 받는다. 눌린 상태로 굳는 걸 막는다.
  const releaseAll = () => {
    for (const key of Object.keys(input) as (keyof MutableInput)[]) input[key] = false;
    stickInput = NO_INPUT;
  };
  window.addEventListener('blur', releaseAll);

  // ─── 캔버스 크기 ────────────────────────────────────────────

  let zoom = 2;
  let viewTilesX = 0;
  let viewTilesY = 0;

  function resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;
    if (cssWidth === 0 || cssHeight === 0) return;

    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);

    // 정수 배율만 쓴다. 소수 배율은 픽셀 아트를 뭉갠다.
    zoom = Math.max(1, Math.min(3, Math.floor(cssWidth / (MIN_VISIBLE_TILES_X * T))));
    viewTilesX = cssWidth / (T * zoom);
    viewTilesY = cssHeight / (T * zoom);
  }

  const observer = new ResizeObserver(resize);
  observer.observe(canvas);
  resize();

  // ─── 루프 ──────────────────────────────────────────────────

  let rafId = requestAnimationFrame(tick);

  function tick(now: number): void {
    // 탭이 백그라운드에 있다가 돌아오면 delta가 몇 초씩 된다.
    // 그대로 쓰면 아바타가 벽을 뚫고 날아간다.
    const delta = Math.min((now - lastFrameAt) / 1000, 0.05);
    lastFrameAt = now;

    update(delta, now);
    render(now);

    rafId = requestAnimationFrame(tick);
  }

  function update(delta: number, now: number): void {
    const state = useChatStore.getState();
    const you = state.you;
    if (you === null) return;

    // 첫 프레임에 서버가 준 자리에서 시작한다.
    me ??= { x: you.x, y: you.y, facing: you.facing, moving: false };

    const moveInput = inputEnabled ? mergeInput(input, stickInput) : NO_INPUT;
    const next = stepActor(me, moveInput, delta, OFFICE_MAP);
    const changed = next !== me;
    me = next;

    if (me.moving) myWalkPhase = (myWalkPhase + delta * 6) % 1;
    else myWalkPhase = 0;

    if (changed) sendMoveThrottled(now);
    updateNearbyObject();
    syncRemotes(state.members, you.userId, delta);
  }

  function sendMoveThrottled(now: number): void {
    if (me === null) return;

    // 좌표를 그대로 비교하면 부동소수 잡음 때문에 늘 다르다.
    // 표시 정밀도까지만 반올림해서 실제로 달라졌을 때만 보낸다.
    const key = `${me.x.toFixed(2)},${me.y.toFixed(2)},${me.facing}`;
    if (key === lastSentKey) return;
    if (now - lastMoveSentAt < MOVE_THROTTLE_MS) return;

    lastSentKey = key;
    lastMoveSentAt = now;
    useChatStore.getState().move(Number(me.x.toFixed(2)), Number(me.y.toFixed(2)), me.facing);
  }

  function updateNearbyObject(): void {
    if (me === null) return;
    const found = objectNear(OFFICE_MAP, me);
    if (found?.id === nearbyObject?.id) return;

    nearbyObject = found;
    onNearbyObjectChange(found);
  }

  function syncRemotes(
    members: readonly { userId: string; x: number; y: number; facing: Facing; online: boolean }[],
    myUserId: string,
    delta: number,
  ): void {
    const seen = new Set<string>();

    for (const member of members) {
      if (member.userId === myUserId || !member.online) continue;
      seen.add(member.userId);

      let remote = remotes.get(member.userId);
      if (remote === undefined) {
        // 처음 보는 사람은 서버가 말한 자리에 그대로 세운다.
        remote = {
          current: { x: member.x, y: member.y },
          target: { x: member.x, y: member.y },
          facing: member.facing,
          walkPhase: 0,
        };
        remotes.set(member.userId, remote);
        continue;
      }

      remote.target = { x: member.x, y: member.y };
      remote.facing = member.facing;

      const before = remote.current;
      remote.current = lerpToward(before, remote.target, delta);

      const movedFar = Math.hypot(remote.target.x - before.x, remote.target.y - before.y) > 0.01;
      remote.walkPhase = movedFar ? (remote.walkPhase + delta * 6) % 1 : 0;
    }

    for (const userId of remotes.keys()) {
      if (!seen.has(userId)) remotes.delete(userId);
    }
  }

  // ─── 그리기 ─────────────────────────────────────────────────

  function render(now: number): void {
    const state = useChatStore.getState();
    if (me === null || state.you === null) return;

    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr * zoom, 0, 0, dpr * zoom, 0, 0);
    ctx.imageSmoothingEnabled = false;

    ctx.fillStyle = PALETTE.wall;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const camera = cameraFor(me);
    ctx.translate(-camera.x * T, -camera.y * T);

    drawGround(camera);
    if (whisperMode) drawWhisperRange(me);
    drawObjects();
    // 카메라가 맵 경계에서 멈추므로, 말풍선이 화면 위로 잘려 나가는지
    // 판단하려면 그리는 쪽이 화면 상단의 월드 좌표를 알아야 한다.
    drawActors(state, now, camera.y * T);
  }

  /** 카메라는 나를 중심에 두되 맵 밖을 보여주지 않는다. */
  function cameraFor(actor: Actor): Point {
    const maxX = Math.max(0, OFFICE_MAP.width - viewTilesX);
    const maxY = Math.max(0, OFFICE_MAP.height - viewTilesY);
    return {
      x: Math.min(Math.max(actor.x - viewTilesX / 2, 0), maxX),
      y: Math.min(Math.max(actor.y - viewTilesY / 2, 0), maxY),
    };
  }

  function drawGround(camera: Point): void {
    const range = visibleTileRange(OFFICE_MAP, camera.x, camera.y, viewTilesX, viewTilesY);
    for (let y = range.y0; y <= range.y1; y++) {
      for (let x = range.x0; x <= range.x1; x++) {
        drawTile(ctx, OFFICE_MAP, x, y);
      }
    }
  }

  function drawObjects(): void {
    for (const object of OFFICE_MAP.objects) {
      drawObject(ctx, object);
      if (object.id === nearbyObject?.id) {
        // 닿는다는 걸 알려준다. 안내 문구는 React 쪽 오버레이가 띄운다.
        ctx.strokeStyle = PALETTE.whisperRingEdge;
        ctx.lineWidth = 1;
        ctx.strokeRect(object.x * T + 1.5, object.y * T + 1.5, T - 3, T - 3);
      }
    }
  }

  /**
   * 속닥이 닿는 범위를 보여준다.
   *
   * 구역 안이면 방 전체가 범위다 — 원을 그리면 거짓말이 된다.
   * 서버의 판정 규칙(proximity.ts)과 정확히 같은 모양이어야 한다.
   */
  function drawWhisperRange(actor: Actor): void {
    const zone = zoneAt(OFFICE_MAP.zones, actor);

    ctx.fillStyle = PALETTE.whisperRing;
    ctx.strokeStyle = PALETTE.whisperRingEdge;
    ctx.lineWidth = 1;

    if (zone !== null) {
      ctx.fillRect(zone.x * T, zone.y * T, zone.width * T, zone.height * T);
      ctx.strokeRect(zone.x * T + 0.5, zone.y * T + 0.5, zone.width * T - 1, zone.height * T - 1);
      return;
    }

    ctx.beginPath();
    ctx.arc(actor.x * T, actor.y * T, WHISPER_RADIUS_TILES * T, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  function drawActors(
    state: ReturnType<typeof useChatStore.getState>,
    now: number,
    viewportTopPx: number,
  ): void {
    const you = state.you!;
    const bubbles = recentBubbles(state, now);

    // y가 큰(아래쪽) 아바타가 나중에 그려져야 앞에 선 것처럼 보인다.
    const drawables: {
      point: Point;
      avatar: Avatar;
      facing: Facing;
      walkPhase: number;
      nickname: string;
      isYou: boolean;
      userId: string;
    }[] = [];

    for (const member of state.members) {
      if (member.userId === you.userId) {
        drawables.push({
          point: { x: me!.x, y: me!.y },
          avatar: member.avatar,
          facing: me!.facing,
          walkPhase: myWalkPhase,
          nickname: member.nickname,
          isYou: true,
          userId: member.userId,
        });
        continue;
      }

      const remote = remotes.get(member.userId);
      if (remote === undefined) continue;

      drawables.push({
        point: remote.current,
        avatar: member.avatar,
        facing: remote.facing,
        walkPhase: remote.walkPhase,
        nickname: member.nickname,
        isYou: false,
        userId: member.userId,
      });
    }

    drawables.sort((a, b) => a.point.y - b.point.y);

    for (const item of drawables) {
      const footX = item.point.x * T;
      const footY = item.point.y * T;
      drawAvatar(ctx, footX, footY, item.avatar, item.facing, item.walkPhase);
      drawNameTag(ctx, footX, footY, item.nickname, item.isYou, viewportTopPx);
    }

    // 말풍선과 이모트는 전부 위에. 아바타에 가리면 읽을 수 없다.
    for (const item of drawables) {
      const footX = item.point.x * T;
      const footY = item.point.y * T;

      const emote = state.emotes[item.userId];
      if (emote !== undefined && now - emote.at < EMOTE_DURATION_MS) {
        drawEmote(ctx, footX, footY, findObject(OFFICE_MAP, emote.objectId)?.emote ?? '…', viewportTopPx);
        continue;
      }

      const bubble = bubbles.get(item.userId);
      if (bubble !== undefined) {
        drawSpeechBubble(ctx, footX, footY, bubble.body, bubble.kind === 'whisper', viewportTopPx);
      }
    }
  }

  /** 최근 메시지를 사람별로 하나씩. 마지막 말만 머리 위에 띄운다. */
  function recentBubbles(
    state: ReturnType<typeof useChatStore.getState>,
    now: number,
  ): Map<string, { body: string; kind: string }> {
    const bubbles = new Map<string, { body: string; kind: string }>();

    // 뒤에서부터 훑다가 시간 창을 벗어나면 멈춘다. 목록 전체를 매 프레임
    // 도는 건 낭비다.
    for (let i = state.messages.length - 1; i >= 0; i--) {
      const message = state.messages[i]!;
      if (now - message.createdAt > SPEECH_BUBBLE_MS) break;
      if (message.deletedAt !== null || message.kind === 'system') continue;
      if (!bubbles.has(message.userId)) {
        bubbles.set(message.userId, { body: message.body, kind: message.kind });
      }
    }
    return bubbles;
  }

  // ─── 핸들 ──────────────────────────────────────────────────

  return {
    destroy() {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', releaseAll);
    },

    setInputEnabled(enabled) {
      inputEnabled = enabled;
      if (!enabled) releaseAll();
    },

    setWhisperMode(on) {
      whisperMode = on;
    },

    setStickVector(dx, dy) {
      stickInput = inputFromVector(dx, dy);
    },

    interactNearby() {
      if (nearbyObject !== null) useChatStore.getState().interact(nearbyObject.id);
    },
  };
}
